import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, Plus, Printer, Search } from 'lucide-react';
import PageHeader from '../../../components/PageHeader';
import { fetchCobranza, fetchCobranzas } from '@/services/cobranzas.js';
import { useOperatorCompactQaScreen } from '@/utils/operatorCompactQa.js';
import AccountStatementModal from '@/components/CuentaCorriente/AccountStatementModal.jsx';

const formatCurrency = (value) =>
  new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(value || 0);

const formatDate = (value) => {
  if (!value) return '—';
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) return `${match[3]}/${match[2]}/${match[1]}`;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleDateString('es-AR');
};

function DetalleCobranzaModal({ cobranzaId, onClose }) {
  const [detalle, setDetalle] = useState(null);
  const [loading, setLoading] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);

  useEffect(() => {
    if (!cobranzaId) return;
    const cargarDetalle = async () => {
      setLoading(true);
      try {
        const data = await fetchCobranza(cobranzaId);
        setDetalle(data);
      } catch {
        setDetalle(null);
      } finally {
        setLoading(false);
      }
    };
    cargarDetalle();
  }, [cobranzaId]);

  if (!cobranzaId) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="max-w-3xl w-full mx-4 rounded-2xl border bg-[rgba(var(--panel))] shadow-[var(--shadow)] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-[rgb(var(--border))]">
          <div>
            <h2 className="text-lg font-semibold text-[rgb(var(--fg))]">Detalle de cobranza</h2>
            <p className="text-sm text-[rgb(var(--muted))]">Resumen del cobro registrado</p>
          </div>
          <button
            onClick={onClose}
            className="px-3 py-1.5 rounded-lg border border-[rgb(var(--border))] text-[rgb(var(--fg))] hover:bg-[rgba(var(--muted),0.3)]"
          >
            Cerrar
          </button>
        </div>

        <div className="p-4 space-y-4">
          {loading && <p className="text-sm text-[rgb(var(--muted))]">Cargando detalle...</p>}
          {!loading && detalle && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs uppercase text-[rgb(var(--muted))]">Cliente</p>
                  <p className="text-sm font-semibold text-[rgb(var(--fg))]">
                    {detalle.cobranza?.cliente_nombre || 'Sin cliente'}
                  </p>
                  <p className="text-xs text-[rgb(var(--muted))]">
                    {detalle.cobranza?.cliente_cuit || 'Sin CUIT'}
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase text-[rgb(var(--muted))]">Fecha</p>
                  <p className="text-sm text-[rgb(var(--fg))]">{formatDate(detalle.cobranza?.fecha_hora)}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-[rgb(var(--muted))]">Recibo</p>
                  <p className="text-sm text-[rgb(var(--fg))]">{detalle.cobranza?.numero_recibo}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-[rgb(var(--muted))]">Total cobrado</p>
                  <p className="text-sm font-semibold text-[rgb(var(--fg))]">
                    {formatCurrency(detalle.cobranza?.total_cobrado)}
                  </p>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-[rgb(var(--fg))] mb-2">Medios de pago</h3>
                <div className="border border-[rgb(var(--border))] rounded-xl overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-[rgba(var(--subpanel))]">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-[rgb(var(--muted))] uppercase">
                          Medio
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-[rgb(var(--muted))] uppercase">
                          Referencia
                        </th>
                        <th className="px-3 py-2 text-right text-xs font-semibold text-[rgb(var(--muted))] uppercase">
                          Importe
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[rgb(var(--border))]">
                      {detalle.pagos?.length ? (
                        detalle.pagos.map((pago) => (
                          <tr key={pago.id}>
                            <td className="px-3 py-2 text-sm text-[rgb(var(--fg))]">{pago.medio_pago}</td>
                            <td className="px-3 py-2 text-sm text-[rgb(var(--fg))]">{pago.referencia || '—'}</td>
                            <td className="px-3 py-2 text-sm text-right text-[rgb(var(--fg))]">
                              {formatCurrency(pago.importe)}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={3} className="px-3 py-3 text-center text-sm text-[rgb(var(--muted))]">
                            No hay medios registrados.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-[rgb(var(--fg))] mb-2">Aplicaciones</h3>
                <div className="border border-[rgb(var(--border))] rounded-xl overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-[rgba(var(--subpanel))]">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-[rgb(var(--muted))] uppercase">
                          Documento
                        </th>
                        <th className="px-3 py-2 text-right text-xs font-semibold text-[rgb(var(--muted))] uppercase">
                          Importe aplicado
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[rgb(var(--border))]">
                      {detalle.aplicaciones?.length ? (
                        detalle.aplicaciones.map((item) => (
                          <tr key={item.id}>
                            <td className="px-3 py-2 text-sm text-[rgb(var(--fg))]">
                              {item.documento_tipo} {item.documento_id || 'A cuenta'}
                            </td>
                            <td className="px-3 py-2 text-sm text-right text-[rgb(var(--fg))]">
                              {formatCurrency(item.monto_aplicado)}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={2} className="px-3 py-3 text-center text-sm text-[rgb(var(--muted))]">
                            No hay imputaciones registradas.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="p-4 border-t border-[rgb(var(--border))] flex justify-end gap-2">
          <button
            type="button"
            onClick={() => setAccountOpen(true)}
            className="px-4 py-2 rounded-xl border border-[rgb(var(--border))] text-[rgb(var(--brand))]"
          >
            Ver cuenta corriente del cliente
          </button>
          <button
            onClick={() => window.print()}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[rgb(var(--brand))] text-white hover:bg-[rgb(var(--brand-700))]"
          >
            <Printer className="w-4 h-4" />
            Imprimir
          </button>
        </div>
      </div>
      <AccountStatementModal
        cliente={{
          id: detalle?.cobranza?.cliente_id,
          razonSocial: detalle?.cobranza?.cliente_nombre,
          cuit: detalle?.cobranza?.cliente_cuit,
        }}
        open={accountOpen}
        onClose={() => setAccountOpen(false)}
      />
    </div>
  );
}

export default function ListadoCobranzas() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    desde: '',
    hasta: '',
    cliente: null,
  });
  const [detalleId, setDetalleId] = useState(null);
  const [cobranzas, setCobranzas] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const cargarCobranzas = async () => {
      setLoading(true);
      try {
        const data = await fetchCobranzas({
          page,
          pageSize,
          search: searchTerm || null,
          clienteId: filters.cliente?.id || null,
          fechaDesde: filters.desde || null,
          fechaHasta: filters.hasta || null,
        });
        const nextTotal = Number(data?.pagination?.total || 0);
        const lastPage = Math.max(1, Math.ceil(nextTotal / pageSize));
        setTotal(nextTotal);
        if (page > lastPage) {
          setPage(lastPage);
          return;
        }
        setCobranzas(data?.items || []);
      } catch {
        setCobranzas([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    };
    cargarCobranzas();
  }, [filters.cliente?.id, filters.desde, filters.hasta, page, pageSize, searchTerm]);

  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const firstItem = total ? (page - 1) * pageSize + 1 : 0;
  const lastItem = Math.min(page * pageSize, total);

  useOperatorCompactQaScreen('Cobranzas', [
    'Header operativo oculto en compacto',
    'Tabla con scroll interno',
  ]);

  return (
    <div className="bg-[rgba(var(--bg))]" data-qa-screen="cobranzas">
      <PageHeader
        title="Cobranzas"
        subtitle="Consulta y administración de cobranzas"
        actions={[
          <button
            key="nuevo"
            onClick={() => navigate('/tesoreria/cobranzas/nueva')}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[rgb(var(--brand))] text-white hover:bg-[rgb(var(--brand-700))] transition-colors focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
          >
            <Plus className="w-4 h-4" />
            Registrar Cobranza
          </button>,
        ]}
      />

      <section className="mx-auto grid max-w-[1400px] grid-cols-12 gap-3 px-4 py-3">
        <div className="col-span-12 rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel))] shadow-[var(--shadow)] overflow-hidden">
          <div
            className="border-b border-[rgb(var(--border))] bg-[rgba(var(--subpanel))] p-2.5"
            data-qa-filters="true"
          >
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-2 lg:flex-row lg:items-center">
                <div className="relative min-w-0 flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[rgb(var(--muted))]" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                    placeholder="Buscar cliente o recibo"
                    aria-label="Buscar por cliente o recibo"
                    className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg))] py-2 pl-10 pr-4 text-sm text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                  />
                </div>
                {(searchTerm || filters.desde || filters.hasta) && (
                  <button
                    onClick={() => {
                      setSearchTerm('');
                      setFilters({ desde: '', hasta: '', cliente: null });
                      setPage(1);
                    }}
                    className="w-full lg:w-auto px-3 py-2 rounded-xl border border-[rgb(var(--border))] text-sm text-[rgb(var(--muted))] hover:text-[rgb(var(--fg))]"
                  >
                    Limpiar filtros
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:flex lg:items-center">
                <label className="flex items-center gap-2 text-sm text-[rgb(var(--muted))]">
                  <span className="shrink-0">Desde</span>
                  <input
                    type="date"
                    value={filters.desde}
                    onChange={(e) => { setFilters({ ...filters, desde: e.target.value }); setPage(1); }}
                    aria-label="Fecha desde"
                    className="min-w-0 flex-1 rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg))] px-3 py-2 text-sm text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                  />
                </label>
                <label className="flex items-center gap-2 text-sm text-[rgb(var(--muted))]">
                  <span className="shrink-0">Hasta</span>
                  <input
                    type="date"
                    value={filters.hasta}
                    onChange={(e) => { setFilters({ ...filters, hasta: e.target.value }); setPage(1); }}
                    aria-label="Fecha hasta"
                    className="min-w-0 flex-1 rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg))] px-3 py-2 text-sm text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                  />
                </label>
              </div>
            </div>
          </div>

          <div
            className="overflow-auto"
            style={{ maxHeight: 'calc(100vh - 220px)' }}
            data-qa-results-scroll="true"
          >
            <table className="hidden min-w-[860px] w-full lg:table">
              <thead
                className="sticky top-0 border-b border-[rgb(var(--border))] bg-[rgba(var(--subpanel),0.98)]"
                data-qa-results-header="true"
              >
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-[rgb(var(--muted))] uppercase">Fecha</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-[rgb(var(--muted))] uppercase">Recibo</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-[rgb(var(--muted))] uppercase">Cliente</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-[rgb(var(--muted))] uppercase">Minuta / medios</th>
                  <th className="px-4 py-2 text-right text-xs font-semibold text-[rgb(var(--muted))] uppercase">Total</th>
                  <th className="px-4 py-2 text-right text-xs font-semibold text-[rgb(var(--muted))] uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[rgb(var(--border))]">
                {loading && (
                  <tr>
                    <td colSpan={6} className="px-4 py-6 text-center text-sm text-[rgb(var(--muted))]">
                      Cargando cobranzas...
                    </td>
                  </tr>
                )}
                {!loading && cobranzas.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-6 text-center text-sm text-[rgb(var(--muted))]">
                      No hay cobranzas registradas con los filtros actuales.
                    </td>
                  </tr>
                )}
                {!loading &&
                  cobranzas.map((cobranza) => (
                    <tr key={cobranza.id} className="hover:bg-[rgba(var(--muted),0.15)]" data-qa-row="true">
                      <td className="px-4 py-2 text-sm text-[rgb(var(--fg))]">{formatDate(cobranza.fecha_hora)}</td>
                      <td className="px-4 py-2 text-sm text-[rgb(var(--fg))]">{cobranza.numero_recibo || '—'}</td>
                      <td className="px-4 py-2 text-sm text-[rgb(var(--fg))]">
                        <div className="font-medium">{cobranza.cliente_nombre || 'Sin cliente'}</div>
                        <div className="text-xs text-[rgb(var(--muted))]">{cobranza.cliente_cuit || ''}</div>
                      </td>
                      <td className="px-4 py-2 text-sm text-[rgb(var(--fg))]">Nº {cobranza.minuta_numero || cobranza.minuta_id || '—'}<div className="text-xs text-[rgb(var(--muted))]">{cobranza.medios_pago || '—'}</div></td>
                      <td className="px-4 py-2 text-right text-sm text-[rgb(var(--fg))]">
                        {formatCurrency(cobranza.total)}
                      </td>
                      <td className="px-4 py-2 text-right text-sm whitespace-nowrap">
                        <button
                          onClick={() => cobranza.recibo_id ? navigate(`/tesoreria/recibos/${cobranza.recibo_id}`) : setDetalleId(cobranza.id)}
                          className="inline-flex items-center gap-2 px-3 py-1 rounded-xl border border-[rgb(var(--border))] text-sm text-[rgb(var(--fg))] hover:bg-[rgba(var(--muted),0.3)]"
                        >
                          <Eye className="w-4 h-4" />
                          Ver recibo
                        </button>
                        <button onClick={() => setDetalleId(cobranza.id)} className="ml-2 inline-flex items-center gap-2 px-3 py-1 rounded-xl border border-[rgb(var(--border))] text-sm">Detalle</button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
            <div className="divide-y divide-[rgb(var(--border))] lg:hidden">
              {!loading && cobranzas.map((cobranza) => (
                <article key={`mobile-${cobranza.id}`} className="space-y-3 p-4" data-qa-row="true">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-xs uppercase tracking-wide text-[rgb(var(--muted))]">{formatDate(cobranza.fecha_hora)}</p>
                      <p className="font-semibold text-[rgb(var(--fg))]">{cobranza.cliente_nombre || 'Sin cliente'}</p>
                      <p className="text-xs text-[rgb(var(--muted))]">{cobranza.cliente_cuit || 'Sin CUIT'}</p>
                    </div>
                    <p className="text-right font-semibold text-[rgb(var(--fg))]">{formatCurrency(cobranza.total)}</p>
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-[rgb(var(--muted))]">
                    <span>Recibo: <strong className="text-[rgb(var(--fg))]">{cobranza.numero_recibo || '—'}</strong></span>
                    <span>Minuta: {cobranza.minuta_numero || cobranza.minuta_id || '—'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => cobranza.recibo_id ? navigate(`/tesoreria/recibos/${cobranza.recibo_id}`) : setDetalleId(cobranza.id)} className="inline-flex items-center gap-2 rounded-xl border border-[rgb(var(--border))] px-3 py-2 text-sm text-[rgb(var(--fg))]"><Eye className="h-4 w-4" /> Ver</button>
                    <details className="relative">
                      <summary className="cursor-pointer list-none rounded-xl border border-[rgb(var(--border))] px-3 py-2 text-sm text-[rgb(var(--muted))]">Más</summary>
                      <div className="absolute right-0 z-10 mt-1 min-w-32 rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--panel))] p-1 shadow-[var(--shadow)]">
                        <button type="button" onClick={() => setDetalleId(cobranza.id)} className="w-full rounded-lg px-3 py-2 text-left text-sm text-[rgb(var(--fg))] hover:bg-[rgba(var(--muted),0.2)]">Detalle</button>
                      </div>
                    </details>
                  </div>
                </article>
              ))}
              {!loading && cobranzas.length === 0 && <p className="p-6 text-center text-sm text-[rgb(var(--muted))]">No hay cobranzas registradas con los filtros actuales.</p>}
              {loading && <p className="p-6 text-center text-sm text-[rgb(var(--muted))]">Cargando cobranzas...</p>}
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[rgb(var(--border))] px-4 py-3 text-sm">
            <span className="text-[rgb(var(--muted))]">Mostrando <strong className="text-[rgb(var(--fg))]">{firstItem}–{lastItem}</strong> de <strong className="text-[rgb(var(--fg))]">{total}</strong> cobranzas</span>
            <div className="flex flex-wrap items-center gap-2">
              <label className="flex items-center gap-2 text-xs text-[rgb(var(--muted))]">Filas<select aria-label="Filas por página" value={pageSize} onChange={(event) => { setPageSize(Number(event.target.value)); setPage(1); }} className="rounded-lg border border-[rgb(var(--border))] bg-[rgb(var(--bg))] px-2 py-1.5 text-sm text-[rgb(var(--fg))]"><option value={20}>20</option><option value={50}>50</option><option value={100}>100</option></select></label>
              <nav aria-label="Paginación de cobranzas" className="flex items-center gap-1">
                <button type="button" onClick={() => setPage((current) => Math.max(1, current - 1))} disabled={page <= 1 || loading} className="rounded-lg border border-[rgb(var(--border))] px-3 py-1.5 disabled:cursor-not-allowed disabled:opacity-40">Anterior</button>
                <span aria-live="polite" className="min-w-24 text-center text-xs text-[rgb(var(--muted))]">Página {page} de {pageCount}</span>
                <button type="button" onClick={() => setPage((current) => Math.min(pageCount, current + 1))} disabled={page >= pageCount || loading} className="rounded-lg border border-[rgb(var(--border))] px-3 py-1.5 disabled:cursor-not-allowed disabled:opacity-40">Siguiente</button>
              </nav>
            </div>
          </div>
        </div>
      </section>

      <DetalleCobranzaModal cobranzaId={detalleId} onClose={() => setDetalleId(null)} />
    </div>
  );
}
